import SimpleModal from "common/Form/Form";
import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import Loader from "layout/Loader";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import InvoiceDownloadIcon from "assets/download-invoice";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { ReactComponent as Inspection } from "assets/SideBarIcons/inspection.svg";
import { useGetInvoiceTemplateService } from "subModule/src/services/invoice/useInvoices";
import { VehicleProfileService } from "subModule/src/core/services";
import {
  GetVehicleInspectionByIdResponse,
  VehicleInspectionByIdResponse,
} from "subModule/src/services/vehicle/interface";
import InspectionImagesCarouselModal from "components/Inspections/InspectionImagesCarouselModal";
import { clearMediaImageCache } from "Helpers/mediaImageCache";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

const getItemImages = (item: any): string[] => {
  const raw = item?.images ?? item?.imageUrls ?? [];
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
};

export default function ViewInspection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "customers",
    moduleLabelKey: "Inspections",
    moduleHref: "/inspections",
    entityFallbackLabelKey: "inspection",
    isEdit: !!params?.id,
    action: "details",
  });
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [loading, setLoading] = useState<boolean>(!!params?.id);
  const [inspection, setInspection] = useState<VehicleInspectionByIdResponse>(
    {},
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [imagesModalOpen, setImagesModalOpen] = useState(false);
  const [activeImages, setActiveImages] = useState<string[]>([]);
  const { data: invoiceTemplatesData, refetch: invoiceTemplatesRefetch } =
    useGetInvoiceTemplateService({
      pageNumber: 1,
      size: 50,
      type: 5,
    });
  const templates = useMemo(() => {
    return (invoiceTemplatesData?.pages?.[0]?.data?.content || []).map(
      (item: { id: number; name: string }) => ({
        value: item.id,
        label: item.name,
      }),
    );
  }, [invoiceTemplatesData]);
  useEffect(() => {
    invoiceTemplatesRefetch();
  }, []);
  useEffect(() => {
    if (params?.id && invoiceTemplatesData) {
      if (loading) getByID();
    }
  }, [params, invoiceTemplatesData]);
  useEffect(() => {
    return () => {
      clearMediaImageCache();
    };
  }, []);
  const statusLabel = (status: number) => {
    if (status === 1) return t("working");
    if (status === 2) return t("needRepair");
    if (status === 3) return t("brokenOrMissing");
    return String(status);
  };
  const getByID = () => {
    VehicleProfileService.getVehicleInspectionByID(Number((params as any)?.id))
      .then((res: GetVehicleInspectionByIdResponse) => {
        if (res?.success) {
          setInspection(res?.data || {});
          captureLabel(
            res?.data?.inspectionFormTitle ||
              res?.data?.attributes?.plateNumber ||
              (params?.id ? String(params.id) : null),
          );
          setLoading(false);
        } else
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const openItemImages = (item: any) => {
    const imgs = getItemImages(item);
    if (!imgs.length) return;
    setActiveImages(imgs);
    setImagesModalOpen(true);
  };
  const closeImagesModal = () => {
    setImagesModalOpen(false);
    setActiveImages([]);
  };
  const vehicleTitle = inspection?.attributes?.plateNumber
    ? `${inspection?.attributes?.makeTitle || ""}-${inspection?.attributes?.modelTitle || ""}${inspection?.attributes?.year ? "-" + inspection?.attributes?.year : ""}`
    : inspection?.attributes?.vehicleVin || " ";
  const filteredSections = useMemo(() => {
    const sections = inspection?.sections || [];
    return sections
      .map((sec: any) => {
        const items = (sec?.items || []).filter(
          (it: any) => it?.status === 1 || it?.status === 2 || it?.status === 3,
        );
        return { ...sec, items };
      })
      .filter((sec: any) => (sec?.items || []).length > 0);
  }, [inspection?.sections]);
  return (
    <div>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate(-1)}
        onSubmit={() => {}}
        heading={t("inspectionDetails")}
        icon={<Inspection height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={false}
        showCreateBtn={false}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={"inspectionDetails"}
          isButtonVisable={false}
          isVisableBtn={false}
          btnclosetext="Back"
          buttonText={""}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          formSubmit={() => {}}
          content={
            <>
              <Box className="text-end">
                <Button
                  id="import-button"
                  variant="contained"
                  disableElevation
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    setAnchorEl(event.currentTarget)
                  }
                  endIcon={<KeyboardArrowDownIcon />}
                  className={`bg-white font-Saira font-[600] text-[13px] h-[31px] px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]`}
                  sx={{
                    outline: "2px solid",
                    color: themeColors?.primary,
                    borderColor: themeColors?.primary,
                  }}
                >
                  {t("download")}
                </Button>
                <DropdownMenu
                  anchorEl={anchorEl}
                  menuItems={(templates || []).map(
                    (template: { value: number; label: string }) => ({
                      label: (
                        <Box className="flex items-center gap-2">
                          <InvoiceDownloadIcon
                            primary={themeColors?.primary}
                            secondary={themeColors?.secondary}
                          />
                          {t(template?.label || "")}
                        </Box>
                      ),
                      onClick: () =>
                        navigate(
                          `/inspection/${params?.id}/template/${template.value}`,
                        ),
                    }),
                  )}
                  onClose={() => setAnchorEl(null)}
                  hoverColor={themeColors?.primary}
                />
              </Box>
              <Grid
                container
                sx={{
                  background: "#fff",
                  borderRadius: "8px",
                  margin: "10px 0",
                  padding: "6px 20px",
                }}
              >
                <Grid container item xs={12} md={4}>
                  <Grid item xs={12} className="text-left">
                    <Typography className="font-Saira text-black font-[600] line-clamp-1 text-[14px]">
                      <h4 className="font-medium mb-0 text-[12px] ff-primary truncate">
                        {vehicleTitle}
                      </h4>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className="text-left pr-5">
                    <Tooltip title={inspection?.attributes?.plateNumber || ""}>
                      <div
                        className="h-[40px] w-[160px] border-2 rounded-md px-2 flex items-center justify-center bg-[#E5E5E5]"
                        style={{ borderColor: themeColors?.primary }}
                      >
                        <Typography
                          className="font-Saira line-clamp-1 font-[700] text-[12px]"
                          sx={{ color: themeColors?.primary }}
                        >
                          <span>
                            {inspection?.attributes?.plateNumber || t("N/A")}
                          </span>
                        </Typography>
                      </div>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={4}>
                  <Grid item xs={12} className="text-left">
                    <Typography
                      className={`font-Saira italic text-txt-color font-[400] text-[12px] flex gap-1 w-[max-content] ${
                        i18n?.language === "ar" || i18n?.language === "ur"
                          ? "flex-row-reverse justify-items-end"
                          : "flex-row justify-items-start"
                      }`}
                    >
                      {t("inspection")}
                      <span>:</span>
                      <span className="font-[700]">
                        {inspection?.inspectionFormTitle || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography className="font-Saira italic text-txt-color font-[500] text-[11px] ms-[-3px]">
                      <span className="font-[700] text-black">
                        {inspection?.attributes?.customerName || ""}
                      </span>
                    </Typography>
                    <Typography className="font-Saira italic text-txt-color font-[500] text-[11px] ms-[-3px]">
                      <span className="font-[700] text-black">
                        {inspection?.attributes?.customerPhone || ""}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={4}>
                  <Grid item xs={12}>
                    <Box
                      className={`font-Saira text-txt-color font-[500] text-[12px] flex gap-1 ${
                        i18n?.language === "ar" || i18n?.language === "ur"
                          ? "flex-row-reverse justify-start"
                          : "flex-row justify-end"
                      }`}
                    >
                      <span className="italic">{t("vehicleNumber")}</span>
                      <span>:</span>
                      <span className="font-Saira italic text-black font-[700]">
                        {inspection?.attributes?.vehicleVin || t("N/A")}
                      </span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      className={`font-Saira text-txt-color text-[9px] font-[500] flex gap-1 ${
                        i18n?.language === "ar" || i18n?.language === "ur"
                          ? "flex-row-reverse justify-start"
                          : "flex-row justify-end"
                      }`}
                    >
                      <span className="italic">{t("station")}</span>
                      <span>:</span>
                      <span className="font-[600]">
                        {inspection?.attributes?.stationName || "-"}
                      </span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      className={`font-Saira text-txt-color text-[9px] font-[500] flex gap-1 ${
                        i18n?.language === "ar" || i18n?.language === "ur"
                          ? "flex-row-reverse justify-start"
                          : "flex-row justify-end"
                      }`}
                    >
                      <span className="font-Saira italic text-txt-color font-[500] text-[9px]">
                        {t("createdAt") + ":"}
                      </span>
                      <span className="font-Saira text-txt-color font-[600] text-[11px] ml-2">
                        {(() => {
                          const date = new Date(inspection?.createdAt as any);
                          return isNaN(date.getTime())
                            ? "-"
                            : new Intl.DateTimeFormat(i18n.language, {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                numberingSystem:
                                  i18n.language === "ur" ||
                                  i18n.language === "ar"
                                    ? "arab"
                                    : "latn",
                              }).format(date);
                        })()}
                      </span>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {/* Sections */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {filteredSections.map((sec: any, secIdx: number) => (
                  <Grid item xs={12} key={sec?.sectionId || secIdx}>
                    <Box sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
                      <Typography className="font-Saira text-start text-black line-clamp-1 font-[700] mb-2 text-[15px] leading-[24px]">
                        {sec?.title || `${t("section")} ${secIdx + 1}`}
                        {sec?.secondaryTitle ? (
                          <span className="ff-arabic font-[500] text-[12px] ms-2">
                            {sec.secondaryTitle}
                          </span>
                        ) : null}
                      </Typography>

                      {(sec?.items || []).map((it: any, idx: number) => {
                        const itemImages = getItemImages(it);
                        return (
                          <Box
                            key={it?.inspectionFormItemId || idx}
                            sx={{
                              border: "1px solid #eee",
                              borderRadius: 2,
                              p: 1.5,
                              mb: 1.5,
                            }}
                          >
                            <Box className="flex justify-between gap-1">
                              <Box>
                                <Typography className="font-Saira font-[700] text-[13px] text-black">
                                  {idx + 1}. {it?.title || "-"}
                                </Typography>
                                {it?.secondaryTitle ? (
                                  <Typography className="ff-arabic font-Saira font-[500] text-[11px] text-txt-color">
                                    {it.secondaryTitle}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Typography
                                className="font-Saira font-[700] text-[11px]"
                                sx={{ color: themeColors?.primary }}
                              >
                                {t("status")}: {statusLabel(it.status)}
                              </Typography>
                            </Box>

                            {it?.notes ? (
                              <Typography className="font-Saira text-[11px] text-black mt-2 whitespace-pre-line text-left">
                                <span className="font-[700]">{t("notes")}:</span>{" "}
                                {it.notes}
                              </Typography>
                            ) : null}

                            {itemImages.length ? (
                              <Box className="mt-2 text-left">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disableElevation
                                  onClick={() => openItemImages(it)}
                                  className="font-Saira font-[600] text-[11px] h-[28px] px-3 rounded-[6px] normal-case"
                                  sx={{
                                    color: themeColors?.primary,
                                    borderColor: themeColors?.primary,
                                  }}
                                >
                                  {t("viewImages")} ({itemImages.length})
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                ))}

                {/* Remarks */}
                {inspection?.remark ? (
                  <Grid item xs={12}>
                    <Box sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
                      <Typography className="font-Saira text-start text-black font-[700] mb-2 text-[15px] leading-[24px]">
                        {t("remarks")}
                      </Typography>
                      <Typography className="font-Saira text-[11px] text-black whitespace-pre-line text-left">
                        {inspection.remark}
                      </Typography>
                    </Box>
                  </Grid>
                ) : null}

                {/* Technicians */}
                {(inspection?.technicians || []).length ? (
                  <Grid item xs={12}>
                    <Box sx={{ background: "#fff", borderRadius: 2, p: 2 }}>
                      <Typography className="font-Saira text-start text-black font-[700] mb-2 text-[15px] leading-[24px]">
                        {t("Technicians")}
                      </Typography>
                      {(inspection.technicians || []).map(
                        (x: any, i: number) => (
                          <Typography
                            key={x?.id || i}
                            className="font-Saira text-[11px] text-black text-left"
                          >
                            {i + 1}. {x?.name || "-"}
                          </Typography>
                        ),
                      )}
                    </Box>
                  </Grid>
                ) : null}
              </Grid>

              <InspectionImagesCarouselModal
                open={imagesModalOpen}
                onClose={closeImagesModal}
                images={activeImages}
              />
            </>
          }
        />
      )}
    </div>
  );
}
